import { onBeforeUnmount, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

/** 全局秒级时钟 — SLA 倒计时 / 入池等待时长 / 会话超时共用一个计时器，避免每行各起一个 */
const now = ref(Date.now());
let timer: number | null = null;
let subscribers = 0;

function ensureTimer(): void {
  if (timer !== null) return;
  timer = window.setInterval(() => {
    now.value = Date.now();
  }, 1000);
}

function releaseTimer(): void {
  if (subscribers > 0 || timer === null) return;
  window.clearInterval(timer);
  timer = null;
}

export function useNow() {
  subscribers += 1;
  ensureTimer();

  onBeforeUnmount(() => {
    subscribers = Math.max(0, subscribers - 1);
    releaseTimer();
  });

  return now;
}

/** 由**后端绝对截止时刻**换算剩余秒数（D-1）；未绑定会话（at<=0）恒为 0，⛔ 不按本地分钟数自造 */
function remainingFrom(at: number): number {
  return at > 0 ? Math.max(0, Math.floor((at - Date.now()) / 1000)) : 0;
}

/**
 * D-1（契约 §2）会话倒计时：取值来源**只有**后端给出的绝对截止时刻
 * （登录响应 `expiresAt` ⇒ `api/auth.ts` 归一 ⇒ `stores/auth.expiresAt`）。
 * ⛔ 不按本地分钟数自造剩余时长；⛔ 不新增独立 idle 计时器（与 SLA／入池共用 `useNow` 秒级时钟）；
 * ⛔ 活动不续期（截止时刻是绝对值，读多少次都不后移）。
 * @param expiresAtMs 绝对截止时刻（epoch ms）；接受 ref／getter ⇒ 重登或 restore 后自动换源重算。
 */
export function useSessionCountdown(expiresAtMs: MaybeRefOrGetter<number>, onExpire: () => void) {
  const clock = useNow();
  const readDeadline = (): number => {
    const value = Number(toValue(expiresAtMs));
    return Number.isFinite(value) && value > 0 ? value : 0;
  };
  const remainingSeconds = ref(remainingFrom(readDeadline()));
  let fired = false;

  const stop = watch(
    [clock, () => readDeadline()],
    ([, at]) => {
      remainingSeconds.value = remainingFrom(at);
      if (at > 0 && remainingSeconds.value === 0 && !fired) {
        fired = true;
        onExpire();
      } else if (at > 0 && remainingSeconds.value > 0) {
        // 绑定了新的（或更晚的）截止时刻 ⇒ 允许下一次到点再触发
        fired = false;
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(stop);

  return { remainingSeconds };
}
